import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import MultiImageUpload from "../../shared/MultiImageUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function FacilityCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      thumbnail_image: null,
      svg: "",
      description: "",
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      thumbnail_image: isEdit
        ? Yup.mixed().nullable()
        : Yup.mixed().required("Image is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("svg", values.svg || "");
      formData.append("description", values.description || "");
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.thumbnail_image) {
        formData.append("thumbnail_image", values.thumbnail_image);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("facilities/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("facilities/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(
            isEdit ? "Facility updated successfully!" : "Facility created successfully!",
          );
          navigate(`/admin/facilities/${id || response.data.data?.id}`);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  const [facilityImages, setFacilityImages] = useState<any[]>([]);

  const fetchFacilityImages = async (facilityId: string) => {
    try {
      const response = await request.get(`facilities/${facilityId}/images`);
      setFacilityImages(
        (response.data.data || []).map((f: any) => ({
          id: f.id,
          fileUrl: imagepathReturn(f.image_url),
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`facilities/${id}`);
          const item = response.data.data;
          formik.setValues({
            title: item.title || "",
            thumbnail_image: null,
            svg: item.svg || "",
            description: item.description || "",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setEditImagePreview(imagepathReturn(item.thumbnail_image || null));
          await fetchFacilityImages(id as string);
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Facility" : "Create New Facility"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/facilities"),
              }}
              primaryButton={{
                label: isEdit ? "Update Facility" : "Create Facility",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="title" label="Title" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="svg"
                    label="SVG"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="description"
                    label="Description"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <FileUpload
                    name="thumbnail_image"
                    formik={formik}
                    label="Upload Thumbnail Image"
                    editImagePreview={editImagePreview}
                  />
                </Box>
              </Box>

              {isEdit && (
                <Box sx={{ marginTop: "24px" }}>
                  <Typography sx={{ fontWeight: 600, color: "#1a1f3a", mb: 1.5 }}>
                    Facility Images
                  </Typography>
                  <MultiImageUpload
                    apiUrl={`facilities/${id}/images`}
                    payload={{ facility_id: id }}
                    existingImages={facilityImages}
                    deleteApiUrl="facility-images"
                    deleteApiRefetch={() => fetchFacilityImages(id as string)}
                    onUploadSuccess={() => fetchFacilityImages(id as string)}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}